import { InjectionToken } from '@angular/core';

export interface PendingAudio {
  key: string;        // storage path = stable id: `${formId}/${afsField}/${slot}/${uuid}.wav`
  formId: number;
  afsField: string;
  slot: 'notes' | 'reason';
  helperId: number;   // needed by flush() to build the scoped transcribe URL
  blob: Blob;         // 16kHz mono WAV
  contentType: string;
}

export interface AudioBlobStore {
  put(p: PendingAudio): Promise<void>;
  get(key: string): Promise<PendingAudio | undefined>;
  delete(key: string): Promise<void>;
  listPending(): Promise<PendingAudio[]>;
}

const DB_NAME = 'afs-audio';
const STORE = 'pending';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'key' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function promisify<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** IndexedDB-backed queue of WAV blobs awaiting transcription. */
export class IdbAudioBlobStore implements AudioBlobStore {
  async put(p: PendingAudio): Promise<void> {
    const db = await openDb();
    try { await promisify(db.transaction(STORE, 'readwrite').objectStore(STORE).put(p)); }
    finally { db.close(); }
  }
  async get(key: string): Promise<PendingAudio | undefined> {
    const db = await openDb();
    try { return (await promisify(db.transaction(STORE, 'readonly').objectStore(STORE).get(key))) as PendingAudio | undefined; }
    finally { db.close(); }
  }
  async delete(key: string): Promise<void> {
    const db = await openDb();
    try { await promisify(db.transaction(STORE, 'readwrite').objectStore(STORE).delete(key)); }
    finally { db.close(); }
  }
  async listPending(): Promise<PendingAudio[]> {
    const db = await openDb();
    try { return (await promisify(db.transaction(STORE, 'readonly').objectStore(STORE).getAll())) as PendingAudio[]; }
    finally { db.close(); }
  }
}

/** In-memory implementation — used in tests (jsdom has no IndexedDB). */
export class InMemoryAudioBlobStore implements AudioBlobStore {
  private map = new Map<string, PendingAudio>();
  async put(p: PendingAudio): Promise<void> { this.map.set(p.key, p); }
  async get(key: string): Promise<PendingAudio | undefined> { return this.map.get(key); }
  async delete(key: string): Promise<void> { this.map.delete(key); }
  async listPending(): Promise<PendingAudio[]> { return [...this.map.values()]; }
}

export const AUDIO_BLOB_STORE = new InjectionToken<AudioBlobStore>('AUDIO_BLOB_STORE', {
  providedIn: 'root',
  factory: () => new IdbAudioBlobStore(),
});
